import { ListRowButton } from '@/components/ui/list-row';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import ListingThumbnail from '@/components/ListingThumbnail';
import { NumText } from '@/components/ui/num-text';
import { GRAMS_PER_LB } from '@/lib/buyRequestParser';
import { formatNumber } from '@/lib/utils';
import { formatLbs, formatThc } from '@/lib/figures';
import { getProductFormLabel } from '@/lib/productFormData';

/**
 * One listing row shared by "Our requests" and "Matched demand":
 * photo + identity is the tap target, figures sit in aligned columns,
 * one primary action, secondary actions stay quiet.
 */
export interface ListingMatchRowProps {
  image?: string | null;
  title: string;
  form?: string | null;
  badge?: ReactNode;
  subline?: ReactNode;
  pricePerG: number | null;
  thcPercent: number | null;
  quantityG: number | null;
  note?: ReactNode;
  href?: string;
  linkState?: unknown;
  onOpen?: () => void;
  secondary?: ReactNode;
  primary?: ReactNode;
}

const perLb = (g: number | null) => (g != null ? `$${formatNumber(Math.round(g * GRAMS_PER_LB), 0)}/lb` : '—');

/** Up to two initials from the strain name, for listings without a photo. */
const monogram = (title: string) =>
  title.split(/[\s\-_/]+/).filter((w) => /[a-z0-9]/i.test(w)).slice(0, 2).map((w) => w.replace(/[^a-z0-9]/gi, '')[0]?.toUpperCase() ?? '').join('');

export default function ListingMatchRow(p: ListingMatchRowProps) {
  const thc = formatThc(p.thcPercent);
  const lbs = formatLbs(p.quantityG);
  const identity = (
    <>
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-muted ring-1 ring-inset ring-border">
        {p.image ? (
          <ListingThumbnail src={p.image} alt={p.title} className="h-full w-full object-cover" />
        ) : monogram(p.title) ? (
          <div className="flex h-full w-full flex-col items-center justify-center leading-none" aria-hidden="true">
            <span className="font-serif text-lg text-foreground/80">{monogram(p.title)}</span>
            {p.form && <span className="mt-0.5 max-w-[2.75rem] truncate text-[9px] uppercase tracking-wide text-muted-foreground">{getProductFormLabel(p.form)}</span>}
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center"><Package className="h-5 w-5 text-muted-foreground" /></div>
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold group-hover:underline underline-offset-2">
          {p.title}{p.form && <span className="font-normal text-muted-foreground"> · {getProductFormLabel(p.form)}</span>}
          {p.badge}
        </p>
        {p.subline && <p className="truncate text-xs text-muted-foreground">{p.subline}</p>}
        {p.note && <p className="truncate text-xs text-muted-foreground">{p.note}</p>}
      </div>
    </>
  );
  const cls = 'group flex min-w-0 flex-1 items-center gap-3 text-left';
  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 px-3 py-3 md:grid-cols-[minmax(0,1fr)_auto_auto]">
      {p.href ? (
        <Link to={p.href} state={p.linkState} className={cls}>{identity}</Link>
      ) : (
        <ListRowButton type="button" className={cn("w-auto p-0", cls)} onClick={p.onOpen}>{identity}</ListRowButton>
      )}
      <div className="grid grid-cols-[5.5rem] justify-items-end text-sm md:grid-cols-[5.5rem_5rem_4.5rem]">
        <span className="font-medium"><NumText role="num-cell">{perLb(p.pricePerG)}</NumText></span>
        <span className="hidden text-muted-foreground md:inline"><NumText role="num-cell">{thc ? `${thc} THC` : '—'}</NumText></span>
        <span className="hidden text-muted-foreground md:inline"><NumText role="num-cell">{lbs ?? '—'}</NumText></span>
      </div>
      <div className="col-span-2 flex items-center justify-between gap-2 md:col-span-1 md:justify-end">
        <span className="text-xs text-muted-foreground md:hidden">
          <NumText role="num-cell">{[thc ? `${thc} THC` : null, lbs].filter(Boolean).join(' · ')}</NumText>
        </span>
        <div className="flex items-center gap-1">{p.secondary}{p.primary}</div>
      </div>
    </li>
  );
}
